import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector } from '../hooks';

import { MandateActions } from '../actions/mandate/mandate.actions';
import { ClientActions } from '../actions/client/client.actions';
import { SectorActions } from '../actions/sector/sector.actions';
import { EmployeeActions } from '../actions/employee/employee.actions';

import type { Mandate } from '../interfaces/client/mandate.interface';
import type { Client } from '../interfaces/client/client.interface';
import type { Sector } from '../interfaces/config/sector.interface';
import type { Employee } from '../interfaces/employee/employee.interface';

import Modal from './components/Modal';
import { num, formatZar } from './lib/mandate-helpers';

// =============================================================================
//  Create mandate modal (v3)
//
//  Minimum-viable v3 capture flow.  Required by the backend:
//    name, client_id, sector_id
//
//  Optional but useful at creation:
//    transaction_type, transaction_size, lead_id, stage, progress, signed,
//    expected_end_date
//
//  Save dispatches MandateActions.createMandate which POSTs to /mandates,
//  refreshes the global mandates store, and (when successful) navigates to
//  the new mandate's v3 detail page.
// =============================================================================

const STAGE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'execution', label: 'Execution' },
  { value: 'due_diligence', label: 'Due Diligence' },
  { value: 'pre_listing', label: 'Pre-listing' },
  { value: 'implementation', label: 'Implementation' },
  { value: 'closing', label: 'Closing' },
  { value: 'reporting', label: 'Reporting' },
  { value: 'structuring', label: 'Structuring' },
  { value: 'field_work', label: 'Field Work' }
];

const TYPE_SUGGESTIONS = [
  'Capital Raise',
  'M&A — Sell-side',
  'M&A — Buy-side',
  'JSE Listing',
  'Restructuring',
  'Disposal',
  'Valuation',
  'BEE Advisory',
  'Due Diligence'
];

interface Props {
  onClose: () => void;
  /** When provided, prefills the client picker (e.g. from a client detail page). */
  defaultClientId?: number | string;
}

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const CreateMandateModal = ({ onClose, defaultClientId }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Pickers load on mount.
  const clients: Client[] = useAppSelector(({ client_store }) => client_store.clients) || [];
  const sectors: Sector[] = useAppSelector(({ sector_store }) => sector_store.sectors) || [];
  const employees: Employee[] =
    useAppSelector(({ employee_store }) => employee_store.employees) || [];

  useEffect(() => {
    if (clients.length === 0) dispatch(ClientActions.loadClients(noOp));
    if (sectors.length === 0) dispatch(SectorActions.loadSectors(noOp));
    if (employees.length === 0) dispatch(EmployeeActions.loadEmployees(noOp));
  }, [dispatch, clients.length, sectors.length, employees.length]);

  const [clientId, setClientId] = useState<string>(defaultClientId ? String(defaultClientId) : '');
  const [name, setName] = useState<string>('');
  const [transactionType, setTransactionType] = useState<string>('');
  const [sectorId, setSectorId] = useState<string>('');
  const [stage, setStage] = useState<string>('execution');
  const [progress, setProgress] = useState<number>(0);
  const [leadId, setLeadId] = useState<string>('');
  const [transactionSize, setTransactionSize] = useState<string>('');
  const [signed, setSigned] = useState<string>(todayIso());
  const [expectedEnd, setExpectedEnd] = useState<string>('');

  const [saving, setSaving] = useState(false);

  const requiredOk = clientId !== '' && name.trim() !== '' && sectorId !== '';

  const handleSave = () => {
    if (!requiredOk) return;
    setSaving(true);
    dispatch(
      MandateActions.createMandate(
        {
          name: name.trim(),
          client_id: Number(clientId),
          sector_id: Number(sectorId),
          stage,
          progress,
          transaction_type: transactionType || undefined,
          transaction_size: transactionSize === '' ? 0 : Number(transactionSize),
          lead_id: leadId === '' ? undefined : Number(leadId),
          signed,
          ...(expectedEnd ? { expected_end_date: expectedEnd } : {})
        } as Partial<Mandate> as Mandate,
        (created?: Mandate) => {
          setSaving(false);
          onClose();
          if (created?.id) navigate(`/mandates/${created.id}`);
        }
      )
    );
  };

  const footer = (
    <>
      <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button
        type="button"
        className="btn primary"
        onClick={handleSave}
        disabled={saving || !requiredOk}
      >
        {saving ? 'Creating…' : 'Create mandate'}
      </button>
    </>
  );

  return (
    <Modal title="New mandate" onClose={onClose} footer={footer} wide>
      <section className="v3-modal-section">
        <h4 className="section-title">Project</h4>

        <div className="field">
          <span className="label">Project name *</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Project Aurora"
            autoFocus
          />
        </div>

        <div className="field row-2">
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Client *</span>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">— Select a client —</option>
              {clients.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Sector *</span>
            <select value={sectorId} onChange={(e) => setSectorId(e.target.value)}>
              <option value="">— Select a sector —</option>
              {sectors.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <span className="label">Transaction type</span>
          <input
            type="text"
            list="v3-create-mandate-types"
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
            placeholder="e.g. Capital Raise"
          />
          <datalist id="v3-create-mandate-types">
            {TYPE_SUGGESTIONS.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </div>
      </section>

      <section className="v3-modal-section">
        <h4 className="section-title">Workflow</h4>

        <div className="field row-2">
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Initial stage</span>
            <select value={stage} onChange={(e) => setStage(e.target.value)}>
              {STAGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Lead partner</span>
            <select value={leadId} onChange={(e) => setLeadId(e.target.value)}>
              <option value="">— Unassigned —</option>
              {employees.map((e) => (
                <option key={e.id} value={String(e.id)}>
                  {e.first_name} {e.last_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <span className="label">Initial progress</span>
          <div className="progress-edit">
            <input
              type="range"
              min={0}
              max={100}
              value={progress}
              onChange={(e) => setProgress(Number(e.target.value))}
            />
            <input
              type="number"
              className="num-input"
              min={0}
              max={100}
              value={progress}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n)) setProgress(Math.max(0, Math.min(100, n)));
              }}
            />
            <span className="pct">{progress}%</span>
          </div>
        </div>
      </section>

      <section className="v3-modal-section">
        <h4 className="section-title">Money & dates</h4>

        <div className="field">
          <span className="label">Transaction size (ZAR)</span>
          <input
            type="number"
            min={0}
            step={1000}
            value={transactionSize}
            onChange={(e) => setTransactionSize(e.target.value)}
            placeholder="e.g. 1800000000"
          />
          {transactionSize !== '' && (
            <span
              className="muted"
              style={{ fontSize: 11.5, marginTop: 2, textTransform: 'none', letterSpacing: 0 }}
            >
              Displays as {formatZar(num(transactionSize))}.
            </span>
          )}
        </div>

        <div className="field row-2">
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Signed</span>
            <input type="date" value={signed} onChange={(e) => setSigned(e.target.value)} />
          </div>
          <div className="field" style={{ gap: 6 }}>
            <span className="label">Target close</span>
            <input
              type="date"
              value={expectedEnd}
              onChange={(e) => setExpectedEnd(e.target.value)}
            />
          </div>
        </div>
      </section>

      <p className="muted" style={{ fontSize: 12, lineHeight: 1.4, marginTop: 4, marginBottom: 0 }}>
        Sets defaults for the rest of the fields (fee, transaction_fee, etc.). Use the Edit button
        on the mandate detail page to fill those in afterwards.
      </p>
    </Modal>
  );
};

export default CreateMandateModal;
